import React, { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * CapitalPnLPanel — real live Capital.com P&L with win-rate, open positions
 * pulled via `cap.list_positions()`, account balance, today vs all-time.
 * Polls every 30s while visible. Includes a sub-account switcher (critical
 * fix — production was pulling the 'preferred' £100k demo instead of the
 * live trading account, showing wrong £101,872 balance).
 */
export default function CapitalPnLPanel({ dismiss, initialData = null }) {
  const [data, setData] = useState(initialData);
  const [err, setErr] = useState(null);
  const [accountsInfo, setAccountsInfo] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const r = await api.get("/jarvis/capital_pnl");
        if (alive) { setData(r.data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || e.message);
      }
    };
    if (!initialData) fetch();
    // Capital live tick — re-fetch every 5 seconds (owner wanted near-
    // realtime, every-second is overkill and will hammer the Capital REST
    // rate-limit; 5s gives them effectively-live + safe headroom).
    const id = setInterval(fetch, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [initialData]);

  // Lazy-load the list of sub-accounts when the user opens the picker.
  const loadAccounts = async () => {
    try {
      const r = await api.get("/jarvis/capital_accounts");
      setAccountsInfo(r.data);
    } catch (e) {
      setAccountsInfo({ enabled: false, accounts: [], error: e.message });
    }
  };

  const switchAccount = async (aid) => {
    setSwitching(true);
    // Close the picker immediately so the user sees confirmation. The
    // re-fetch happens in the background; refreshed numbers land in the
    // tile grid when ready.
    setShowPicker(false);
    try {
      await api.post("/jarvis/capital_set_account", { account_id: aid });
      const r = await api.get("/jarvis/capital_pnl");
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSwitching(false);
    }
  };

  if (err) {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-capital_pnl">
        {dismiss && (
          <button
            onClick={dismiss}
            className="absolute top-2 right-2 z-30 w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center text-xl font-display text-[var(--rc-gold)] border border-[var(--rc-gold)] bg-[rgba(0,4,12,0.85)] hover:bg-[var(--rc-gold)] hover:text-black active:bg-[var(--rc-gold)] active:text-black transition-colors"
            data-testid="dismiss-capital-pnl-err"
            aria-label="Close panel"
            title="Close panel (Esc)"
          >×</button>
        )}
        <div className="text-sm text-red-400">⚠ {err}</div>
      </div>
    );
  }
  if (!data) return <div className="stage-in rc-panel p-5">Loading Capital…</div>;
  const open = data.open_positions || [];
  const acct = data.account || {};
  return (
    <div className="stage-in rc-panel relative p-5 max-w-4xl w-full" data-testid="stage-capital_pnl">
      {/* Owner Feb 2026: "no cross on Capital P&L card". Upgraded the tiny
          ghost-× to the standard prominent gold-bordered close button used
          everywhere else, with a 44px touch target for mobile. */}
      {dismiss && (
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 z-30 w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center text-xl font-display text-[var(--rc-gold)] border border-[var(--rc-gold)] bg-[rgba(0,4,12,0.85)] hover:bg-[var(--rc-gold)] hover:text-black active:bg-[var(--rc-gold)] active:text-black transition-colors"
          data-testid="dismiss-capital-pnl"
          aria-label="Close panel"
          title="Close panel (Esc)"
        >×</button>
      )}
      <div className="flex items-start justify-between gap-3 mb-3 pr-14">
        <div className="font-display tracking-widest text-[var(--rc-gold)] rc-glow-text uppercase text-sm">
          // CAPITAL.COM · LIVE P&L · {data.trade_mode?.toUpperCase()}
        </div>
        <button
          type="button"
          onClick={() => { if (!showPicker) loadAccounts(); setShowPicker(!showPicker); }}
          className="border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] px-2 py-1 text-[10px] tracking-widest text-[var(--rc-gold)] uppercase"
          data-testid="cap-switch-account"
        >
          {showPicker ? "× Close" : "↻ Switch Account"}
        </button>
      </div>
      {/* Active account banner — shows EXACTLY which sub-account the figures are for */}
      {acct.accountId && (
        <div className="mb-3 border border-[var(--rc-gold-dim)] bg-[rgba(255,184,0,0.04)] px-3 py-2 text-[11px] tracking-wide" data-testid="cap-active-account">
          <span className="opacity-60">ACCOUNT</span>{" "}
          <span className="text-[var(--rc-gold)]">{acct.accountName || acct.accountId}</span>
          <span className="opacity-50"> · {acct.accountType || "?"} · {acct.currency || "?"} · ID {acct.accountId}</span>
        </div>
      )}
      {showPicker && (
        <div className="mb-4 border border-[var(--rc-gold-dim)] bg-black/60 p-3" data-testid="cap-account-picker">
          <div className="text-[10px] opacity-60 tracking-widest mb-2">PICK WHICH SUB-ACCOUNT R.A.W. READS FROM</div>
          {!accountsInfo && <div className="text-[11px] opacity-60">Loading…</div>}
          {accountsInfo?.accounts?.length > 0 && (
            <div className="grid grid-cols-1 gap-2">
              {accountsInfo.accounts.map((a) => {
                const isActive = String(a.accountId) === String(accountsInfo.active);
                return (
                  <button
                    key={a.accountId}
                    type="button"
                    disabled={switching}
                    onClick={() => switchAccount(a.accountId)}
                    className={`text-left border px-3 py-2 text-[11px] transition ${isActive ? "border-[var(--rc-gold)] bg-[rgba(255,184,0,0.1)]" : "border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)]"}`}
                    data-testid={`cap-account-${a.accountId}`}
                  >
                    <div className="flex justify-between items-center gap-3">
                      <div>
                        <div className="text-[var(--rc-gold)]">{a.accountName || a.accountId} {isActive && "← active"}</div>
                        <div className="opacity-50 text-[10px]">ID {a.accountId} · {a.currency}</div>
                      </div>
                      <div className="text-right">
                        <div className="tabular-nums">£{Number(a.balance ?? 0).toFixed(2)}</div>
                        <div className="opacity-50 text-[10px]">avail £{Number(a.available ?? 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {accountsInfo?.accounts?.length === 0 && (
            <div className="text-[11px] opacity-60">No sub-accounts returned. {accountsInfo.error}</div>
          )}
        </div>
      )}
      {!data.capital_enabled && (
        <div className="mb-3 border border-orange-500/60 bg-orange-500/10 px-3 py-2 text-[11px]" data-testid="capital-not-connected">
          ⚠ Capital.com not configured. Add your CAPITAL_API_KEY + CAPITAL_PASSWORD to backend/.env.
        </div>
      )}
      {/* HEADLINE strip: the BIG numbers — overall revenue + live running P&L.
          These are what the owner actually wants to glance at: "how much have
          I made all-time" and "what's my open position running".
          Owner Feb 2026: long figures (e.g. "£-2547.30") were spilling
          outside their boxes — shrink the font on narrow widths + force
          wrap so it never overflows. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="cap-headline">
        <div className="border-2 border-[var(--rc-gold)] bg-[rgba(212,175,55,0.06)] p-4 rounded min-w-0 overflow-hidden">
          <div className="rc-label text-[9px] opacity-70">ALL-TIME REVENUE (REALISED)</div>
          <div className={`font-display text-2xl sm:text-3xl tabular-nums mt-1 break-words leading-tight ${num(data.total_pnl_gbp) >= 0 ? "rc-glow-text" : "text-red-400"}`}>
            £{num(data.total_pnl_gbp).toFixed(2)}
          </div>
          <div className="text-[10px] opacity-50 mt-1">{data.closed_count ?? 0} closed trades · {data.wins ?? 0}W / {data.losses ?? 0}L</div>
        </div>
        <div className="border-2 border-[var(--rc-gold-dim)] bg-[rgba(0,4,12,0.4)] p-4 rounded min-w-0 overflow-hidden">
          <div className="rc-label text-[9px] opacity-70">RUNNING (UNREALISED) P&L</div>
          <div className={`font-display text-2xl sm:text-3xl tabular-nums mt-1 break-words leading-tight ${num(data.open_upl_gbp) >= 0 ? "rc-glow-text" : "text-red-400"}`}>
            £{num(data.open_upl_gbp).toFixed(2)}
          </div>
          <div className="text-[10px] opacity-50 mt-1">{data.open_count ?? 0} open position{data.open_count === 1 ? "" : "s"}</div>
        </div>
        <div className="border-2 border-[var(--rc-gold-dim)] bg-[rgba(0,4,12,0.4)] p-4 rounded min-w-0 overflow-hidden">
          <div className="rc-label text-[9px] opacity-70">GRAND TOTAL (REALISED + RUNNING)</div>
          <div className={`font-display text-2xl sm:text-3xl tabular-nums mt-1 break-words leading-tight ${(num(data.total_pnl_gbp) + num(data.open_upl_gbp)) >= 0 ? "rc-glow-text" : "text-red-400"}`}>
            £{(num(data.total_pnl_gbp) + num(data.open_upl_gbp)).toFixed(2)}
          </div>
          <div className="text-[10px] opacity-50 mt-1">today realised £{num(data.today_pnl_gbp).toFixed(2)}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Tile label="ACCOUNT BAL" value={acct.balance ? `£${acct.balance.balance?.toFixed?.(2) ?? acct.balance.toFixed?.(2) ?? acct.balance}` : "—"} testId="cap-balance" />
        <Tile label="AVAILABLE" value={acct.balance?.available != null ? `£${Number(acct.balance.available).toFixed(2)}` : "—"} testId="cap-equity" />
        <Tile label="OPEN POSITIONS" value={data.open_count ?? 0} glow testId="cap-open" />
        <Tile
          label="LIVE UNREAL P&L"
          value={`£${num(data.open_upl_gbp).toFixed(2)}`}
          tone={num(data.open_upl_gbp) >= 0 ? "ok" : "bad"}
          testId="cap-upl"
        />
        <Tile
          label="TODAY P&L"
          value={`£${num(data.today_pnl_gbp).toFixed(2)}`}
          tone={num(data.today_pnl_gbp) >= 0 ? "ok" : "bad"}
          testId="cap-today"
        />
        <Tile label="ALL-TIME P&L" value={`£${num(data.total_pnl_gbp).toFixed(2)}`} tone={num(data.total_pnl_gbp) >= 0 ? "ok" : "bad"} testId="cap-total" />
        <Tile label="WIN RATE" value={`${num(data.win_rate_pct).toFixed(1)}%`} testId="cap-wr" />
        <Tile label={`AVG WIN · ${data.wins ?? 0}W / ${data.losses ?? 0}L`} value={<><span>£{num(data.avg_win_gbp).toFixed(2)}</span><span className="opacity-50 mx-1">/</span><span>£{num(data.avg_loss_gbp).toFixed(2)}</span></>} testId="cap-avgw" />
        {data.streak_count > 0 && (
          <Tile
            label={`${data.streak_kind === "win" ? "WIN STREAK 🔥" : "LOSS STREAK 🥶"}`}
            value={`${data.streak_count} in a row`}
            tone={data.streak_kind === "win" ? "ok" : "bad"}
            testId="cap-streak"
          />
        )}
        {data.best_trade && (
          <Tile
            label={`BEST TRADE · ${data.best_trade.symbol}`}
            value={`£${num(data.best_trade.pnl).toFixed(2)}`}
            tone="ok"
            testId="cap-best"
          />
        )}
        {data.worst_trade && (
          <Tile
            label={`WORST TRADE · ${data.worst_trade.symbol}`}
            value={`£${num(data.worst_trade.pnl).toFixed(2)}`}
            tone="bad"
            testId="cap-worst"
          />
        )}
      </div>
      {data.pnl_source === "none" && data.capital_enabled && (
        <div className="mb-3 border border-orange-500/40 bg-orange-500/5 px-3 py-2 text-[11px] opacity-80" data-testid="capital-no-history">
          ⚠ No closed trades yet — once you close a position on Capital.com, realised P&L will appear here.
          {data.transactions_error && <div className="opacity-60 mt-1">Capital history fetch: {data.transactions_error}</div>}
        </div>
      )}
      {open.length > 0 && (
        <>
          <div className="rc-label text-[10px] mb-2">OPEN POSITIONS</div>
          <div className="space-y-1 mb-3">
            {open.map((p, i) => {
              const pos = p.position || p;
              const mkt = p.market || {};
              const upl = pos.upl ?? pos.profit ?? 0;
              return (
                <div key={i} className="grid grid-cols-5 gap-2 text-xs border-l-2 border-[var(--rc-gold)] pl-3 py-1.5" data-testid={`cap-pos-${i}`}>
                  <div className="font-display">{mkt.instrumentName || mkt.epic || pos.epic || "?"}</div>
                  <div className={pos.direction === "BUY" ? "text-green-400" : "text-red-400"}>{pos.direction}</div>
                  <div className="tabular-nums">@ {pos.level?.toFixed?.(2) ?? "—"}</div>
                  <div className="tabular-nums opacity-70">stop {pos.stopLevel?.toFixed?.(2) ?? "—"}</div>
                  <div className={`tabular-nums font-display ${upl >= 0 ? "rc-glow-text text-green-400" : "text-red-400"}`}>£{Number(upl).toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className="text-[9px] opacity-50 text-right">refreshes every 30s</div>
    </div>
  );
}

function Tile({ label, value, glow, tone, testId }) {
  // Owner Feb 2026: "numbers protrude into the next box". Tiles had a
  // fixed text-base width and no overflow handling, so long values like
  // "£-2547.30" or composite "£21.26 / £-36.12" spilled outside their
  // boxes. Fix: shrink the font on narrow widths and force values to
  // break inside their cell (never overflow horizontally).
  let cls = "font-display text-sm sm:text-base mt-1 tabular-nums break-words leading-tight";
  if (tone === "ok") cls += " text-green-400";
  else if (tone === "bad") cls += " text-red-400";
  else if (glow) cls += " rc-glow-text";
  return (
    <div className="border border-[var(--rc-gold-dim)] p-3 min-w-0 overflow-hidden" data-testid={testId}>
      <div className="rc-label text-[9px] truncate">{label}</div>
      <div className={cls}>{value}</div>
    </div>
  );
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
